import type React from 'react';
import type { PosProduct } from '../../posProduct';
import type { AddByBarcodeOutcome, AddProductOutcome } from '../../useSeller';

/** A notice key for the component to translate and toast; never a string itself. */
export type SearchEnterNoticeKey =
    | 'seller.barcodeNotFound'
    | 'seller.barcodeOutOfStock'
    | 'seller.barcodeAtLimit';

type UseSearchEnterToAddParams = {
    search: string;
    setSearch: (value: string) => void;
    filteredProducts: PosProduct[];
    addByBarcode: (code: string) => AddByBarcodeOutcome;
    addProduct: (product: PosProduct) => AddProductOutcome;
    /**
     * The product grid's own list-box node (bos-seller#55). Focused directly
     * rather than queried for a tile: a scrolled virtualized grid doesn't
     * have the first one in the DOM, and react-aria's own collection focus
     * (the first tile, or the previously-focused one) already knows how to
     * scroll a not-yet-rendered target into view first.
     */
    productGridRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * Search `Enter`'s own rules (bos-seller#53). The text is tried as a barcode
 * first (`useSeller().addByBarcode`, which never reads the search box). When
 * it is no barcode, the search results decide: one result is added the way a
 * scan would (`addProduct`); several with something sellable move focus into
 * the grid; several all out of stock say so; none is "not found".
 *
 * An add or a variant-picker open clears the search text; every other
 * outcome keeps it and resolves to a notice key. Never calls `t()` or shows a
 * toast itself: `onSearchKeyDown`'s return value is the notice key (or
 * `null`) for the caller — the render-only `.tsx` — to translate and toast.
 */
export const useSearchEnterToAdd = ({
    search,
    setSearch,
    filteredProducts,
    addByBarcode,
    addProduct,
    productGridRef,
}: UseSearchEnterToAddParams) => {
    const settle = (
        outcome: AddProductOutcome,
    ): SearchEnterNoticeKey | null => {
        switch (outcome.type) {
            case 'added':
            case 'variantPicker':
                setSearch('');
                return null;
            case 'outOfStock':
                return 'seller.barcodeOutOfStock';
            case 'atLimit':
                return 'seller.barcodeAtLimit';
        }
    };

    const onSearchKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
    ): SearchEnterNoticeKey | null => {
        if (event.key !== 'Enter') {
            return null;
        }
        const code = search.trim();
        if (code === '') {
            return null;
        }
        event.preventDefault();

        const outcome = addByBarcode(code);
        if (outcome.type !== 'notFound') {
            return settle(outcome);
        }
        const [sole, ...others] = filteredProducts;
        if (!sole) {
            return 'seller.barcodeNotFound';
        }
        if (others.length === 0) {
            return settle(addProduct(sole));
        }
        if (filteredProducts.some((product) => product.stock > 0)) {
            productGridRef.current?.focus();
            return null;
        }
        return 'seller.barcodeOutOfStock';
    };

    return { onSearchKeyDown };
};
